/* eslint react-hooks/exhaustive-deps: "warn" */
import type { CSSProperties } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './FddHero.module.scss';

type Translate = (key: string) => string;

type FinancialRow = {
  label: string;
  values: string[];
  heights?: number[];
  negative?: boolean[];
};

const YEARS = [
  '2017-12-31',
  '2018-12-31',
  '2019-12-31',
  '2020-12-31',
  '2021-12-31',
  '2022-12-31',
  '2023-12-31',
  '2024-12-31',
];

const FINANCIAL_ROWS: FinancialRow[] = [
  { label: 'Reporting period', values: ['12', '12', '12', '12', '12', '12', '12', '12'] },
  { label: 'Currency', values: ['USD', 'USD', 'USD', 'USD', 'USD', 'USD', 'USD', 'USD'] },
  { label: 'Consolidated A/cs', values: ['Y', 'Y', 'Y', 'Y', 'Y', 'Y', 'Y', 'Y'] },
  {
    label: 'Turnover',
    values: ['6,102.4', '6,588.9', '7,204.1', '7,843.7', '9,388.7', '10,105.7', '9,493.7', '9,446.5'],
    heights: [60, 65, 71, 78, 93, 100, 94, 93],
  },
  {
    label: 'Cost of sales',
    values: ['5,020.8', '5,401.2', '5,930.6', '6,455.3', '7,448.4', '8,153.5', '7,841.6', '7,720.2'],
    heights: [62, 66, 73, 79, 91, 100, 96, 95],
  },
  {
    label: 'EBITDA',
    values: ['268.9', '301.4', '344.2', '385.8', '795.7', '780.2', '494.5', '614.4'],
    heights: [34, 38, 43, 48, 100, 98, 62, 77],
  },
  {
    label: 'EBITDA margin',
    values: ['4.4%', '4.6%', '4.8%', '4.9%', '8.5%', '7.7%', '5.2%', '6.5%'],
    heights: [52, 54, 56, 58, 100, 91, 61, 76],
  },
  {
    label: 'Profit after tax',
    values: ['88.2', '-41.6', '102.7', '-220.5', '250.0', '196.4', '-35.4', '126.1'],
    heights: [35, 17, 41, 88, 100, 79, 14, 50],
    negative: [false, true, false, true, false, false, true, false],
  },
  {
    label: 'Total assets',
    values: ['5,904.2', '6,320.5', '6,880.1', '7,337.9', '7,890.4', '7,949.3', '7,749.3', '7,498.7'],
    heights: [74, 80, 87, 92, 99, 100, 97, 94],
  },
  {
    label: 'Total liabilities',
    values: ['5,010.7', '5,338.2', '5,801.9', '6,242.3', '6,441.8', '6,287.2', '6,083.4', '5,759.6'],
    heights: [78, 83, 90, 97, 100, 98, 94, 89],
  },
];

const getStats = (t: Translate) => [
  { top: t('Filed'), bottom: t('accounts, as submitted') },
  { top: '10 years', bottom: t('of filing history') },
  { top: t('Per figure'), bottom: t('filing reference') },
];

const MARQUEE_ITEMS = [
  { flag: '🇬🇧', name: 'Companies House', sub: 'United Kingdom' },
  { flag: '🇺🇸', name: 'Delaware Division of Corporations', sub: 'United States' },
  { flag: '🇬🇧', name: 'London Stock Exchange', sub: 'Exchange' },
  { flag: '🇩🇪', name: 'Handelsregister', sub: 'Germany' },
  { flag: '🇫🇷', name: 'Registre National des Entreprises', sub: 'France' },
  { flag: '🇺🇸', name: 'New York Stock Exchange', sub: 'Exchange' },
  { flag: '🇮🇹', name: 'Registro Imprese', sub: 'Italy' },
  { flag: '🇪🇸', name: 'Registro Mercantil', sub: 'Spain' },
  { flag: '🇺🇸', name: 'Nasdaq', sub: 'Exchange' },
  { flag: '🇳🇱', name: 'KVK Handelsregister', sub: 'Netherlands' },
  { flag: '🇧🇪', name: 'Kruispuntbank van Ondernemingen', sub: 'Belgium' },
  { flag: '🇩🇪', name: 'Deutsche Börse', sub: 'Exchange' },
  { flag: '🇮🇪', name: 'Companies Registration Office', sub: 'Ireland' },
  { flag: '🇨🇭', name: 'Zefix Handelsregister', sub: 'Switzerland' },
  { flag: '🇫🇷', name: 'Euronext', sub: 'Exchange' },
  { flag: '🇦🇹', name: 'Firmenbuch', sub: 'Austria' },
  { flag: '🇱🇺', name: 'Registre de Commerce et des Sociétés', sub: 'Luxembourg' },
  { flag: '🇨🇭', name: 'SIX Swiss Exchange', sub: 'Exchange' },
  { flag: '🇸🇪', name: 'Bolagsverket', sub: 'Sweden' },
  { flag: '🇳🇴', name: 'Brønnøysundregistrene', sub: 'Norway' },
  { flag: '🇯🇵', name: 'Japan Exchange Group', sub: 'Exchange' },
  { flag: '🇩🇰', name: 'Det Centrale Virksomhedsregister', sub: 'Denmark' },
  { flag: '🇫🇮', name: 'Patentti- ja rekisterihallitus', sub: 'Finland' },
  { flag: '🇭🇰', name: 'Hong Kong Exchanges', sub: 'Exchange' },
  { flag: '🇵🇱', name: 'Krajowy Rejestr Sądowy', sub: 'Poland' },
  { flag: '🇨🇿', name: 'Obchodní rejstřík', sub: 'Czechia' },
  { flag: '🇸🇬', name: 'Singapore Exchange', sub: 'Exchange' },
  { flag: '🇷🇴', name: 'Registrul Comerțului', sub: 'Romania' },
  { flag: '🇨🇦', name: 'Corporations Canada', sub: 'Canada' },
  { flag: '🇦🇺', name: 'Australian Securities Exchange', sub: 'Exchange' },
  { flag: '🇧🇷', name: 'Receita Federal · CNPJ', sub: 'Brazil' },
  { flag: '🇦🇺', name: 'Australian Securities & Investments Commission', sub: 'Australia' },
  { flag: '🇨🇦', name: 'Toronto Stock Exchange', sub: 'Exchange' },
  { flag: '🇳🇿', name: 'New Zealand Companies Office', sub: 'New Zealand' },
  { flag: '🇸🇬', name: 'Accounting & Corporate Regulatory Authority', sub: 'Singapore' },
  { flag: '🇮🇳', name: 'National Stock Exchange of India', sub: 'Exchange' },
  { flag: '🇭🇰', name: 'Companies Registry', sub: 'Hong Kong' },
  { flag: '🇯🇵', name: 'National Tax Agency', sub: 'Japan' },
  { flag: '🇰🇷', name: 'Korea Exchange', sub: 'Exchange' },
  { flag: '🇮🇳', name: 'Ministry of Corporate Affairs', sub: 'India' },
  { flag: '🇿🇦', name: 'Companies and Intellectual Property Commission', sub: 'South Africa' },
  { flag: '🇿🇦', name: 'Johannesburg Stock Exchange', sub: 'Exchange' },
];

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M3 8h10M9 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const FddHero = () => {
  const { t } = useInt();
  const stats = getStats(t);

  return (
    <div className={styles.root}>
      <Section background="linear-gradient(165deg, #0d1220 0%, #05070c 100%)" className={styles.heroSection}>
        <div className={styles.grid}>
          <div className={styles.copy}>
            <TitleBlock
              size="page"
              align="left"
              theme="dark"
              title={t('Assess private companies on their filed accounts.')}
              description={t(
                'We collect the original filings from 400+ government registers, read the scanned and paper ones with OCR, and extract the line items. Every figure comes back with its source, its filing and the date it was last updated.',
              )}
            />

            <div className={styles.actions}>
              <Button type="blue" size="xxl" href="/free-trial" suffix={<ArrowIcon />}>
                {t("See a company's accounts")}
              </Button>

              <Button type="white-outline" size="xxl" href="/data-dictionary" suffix={<ArrowIcon />}>
                {t('Data dictionary')}
              </Button>
            </div>

            <div className={styles.stats}>
              {stats.map((s, i) => (
                <div key={i} className={styles.stat}>
                  <span className={styles.statTop}>{s.top}</span>
                  <span className={styles.statBottom}>{s.bottom}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.card} aria-label="Eight years of filed financials">
            <div className={styles.cardBar}>
              <span className={styles.cardBarLabel}>Summary</span>
              <div className={styles.cardBarMeta}>
                <span className={styles.cardBarMetaText}>Filed accounts · 8 years</span>
                <span className={styles.cardNav} aria-hidden="true">
                  <button className={styles.arrow} aria-label="Previous">
                    ‹
                  </button>
                  <button className={cn(styles.arrow, styles.arrowOn)} aria-label="Next">
                    ›
                  </button>
                </span>
              </div>
            </div>

            <div className={styles.cardBody}>
              <div className={cn(styles.row, styles.rowHead)}>
                <span className={styles.rowLabel} />
                <span className={styles.rowWindow}>
                  <span className={styles.rowTrack}>
                    {YEARS.map((y) => (
                      <span key={y} className={styles.cellHead}>
                        {y}
                      </span>
                    ))}
                  </span>
                </span>
                <span className={styles.rowTrendHead}>Trend</span>
              </div>

              {FINANCIAL_ROWS.map((row) => (
                <div key={row.label} className={styles.row}>
                  <span className={styles.rowLabel}>{row.label}</span>
                  <span className={styles.rowWindow}>
                    <span className={styles.rowTrack}>
                      {row.values.map((v, i) => (
                        <span key={i} className={cn(styles.cellValue, row.negative?.[i] && styles.cellValueNeg)}>
                          {v}
                        </span>
                      ))}
                    </span>
                  </span>
                  <span className={styles.rowTrend}>
                    {row.heights ? (
                      <span className={styles.spark}>
                        {row.heights.map((h, i) => (
                          <i
                            key={i}
                            style={{ '--h': `${h}%` } as CSSProperties}
                            className={cn(styles.sparkBar, row.negative?.[i] && styles.sparkBarDown)}
                          />
                        ))}
                      </span>
                    ) : (
                      <span className={styles.trendNone}>—</span>
                    )}
                  </span>
                </div>
              ))}
            </div>

            <div className={styles.cardFoot}>
              <span className={styles.cardSource}>
                Companies House · full accounts · USD millions · last updated 30 Apr 2026
              </span>
              <a href="/book-a-demo" className={styles.cardLink}>
                Open filing ↗
              </a>
            </div>
          </div>
        </div>
      </Section>

      <div className={styles.marqueeWrap}>
        <div className={styles.marqueeLabel}>
          {t('Connected directly to 400+ official registries and stock exchanges')}
        </div>
        <div className={styles.marqueeMask}>
          <div className={styles.marqueeTrack}>
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
              <span key={i} className={styles.marqueeItem}>
                <span className={styles.marqueeFlag} aria-hidden="true">
                  {item.flag}
                </span>
                <span className={styles.marqueeText}>
                  <b>{item.name}</b>
                  <i>{item.sub}</i>
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
